import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { exhaustMap, filter, map, of, switchMap, tap } from 'rxjs';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { isCacheValid, markCached } from '@ps/utils';
import { MANDATORY_PM_EMAILS_INVOICE_TURNED_OFF_MESSAGE } from '../models/company-buyer-config.model';
import { CompanyService } from '../services/company.service';
import { CompanySupplierSettingsService } from '../services/company-supplier-settings.service';
import { CompanyBuyerConfigService } from '../services/company-buyer-config.service';
import { CompanyBuyerConfigNotificationService } from '../services/company-buyer-config-notification.service';
import { SupplierSegmentationService } from '../services/supplier-segmentation.service';
import { CompanyActions } from './company.actions';
import { selectLoadContext } from './company.selectors';
import { COMPANIES_CACHE_TTL } from './company-state.model';

@Injectable()
export class CompanyEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(CompanyService);
  private readonly _supplierSettingsService = inject(
    CompanySupplierSettingsService,
  );
  private readonly _buyerConfigService = inject(CompanyBuyerConfigService);
  private readonly _buyerConfigNotificationService = inject(
    CompanyBuyerConfigNotificationService,
  );
  private readonly _segmentationService = inject(SupplierSegmentationService);
  private readonly _store = inject(Store);
  private readonly _toastService = inject(ToastService);

  public readonly loadCompanies$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        CompanyActions.loadCompanies,
        CompanyActions.setSearch,
        CompanyActions.setPage,
      ),
      concatLatestFrom(() => this._store.select(selectLoadContext)),
      switchMap(([, { currentPage, search, limit, pageCache }]) => {
        const cachedPage = pageCache[currentPage];
        if (
          cachedPage &&
          isCacheValid(cachedPage.cachedAt, COMPANIES_CACHE_TTL)
        ) {
          return of(
            CompanyActions.loadCompaniesSuccess({
              companies: cachedPage.companies,
              count: cachedPage.totalCount,
              page: currentPage,
              cachedAt: cachedPage.cachedAt,
            }),
          );
        }
        return this._service
          .getAll(currentPage, limit ?? undefined, search)
          .pipe(
            map(({ companies, count }) =>
              CompanyActions.loadCompaniesSuccess({
                companies,
                count,
                page: currentPage,
                cachedAt: markCached(),
              }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  CompanyActions.loadCompaniesFailure({ error: error.message }),
                ),
              {
                notify: true,
                customMessage: 'Failed to load companies. Please try again.',
              },
            ),
          );
      }),
    ),
  );

  public readonly loadDetail$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.selectCompany, CompanyActions.reloadDetail),
      switchMap(({ id }) =>
        this._service.companyDetail(id).pipe(
          map((detail) => CompanyActions.loadDetailSuccess({ detail })),
          this.psCatchError(
            (error) =>
              of(CompanyActions.loadDetailFailure({ error: error.message })),
            {
              notify: true,
              customMessage:
                'Failed to load company details. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly loadSupplierSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.loadSupplierSettings),
      switchMap(({ id }) =>
        this._supplierSettingsService.supplierSettings(id).pipe(
          map((settings) =>
            CompanyActions.loadSupplierSettingsSuccess({ settings }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.loadSupplierSettingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to load supplier settings. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly loadOperatorUsers$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.loadOperatorUsers),
      switchMap(() =>
        this._service.operatorUsers().pipe(
          map((operatorUsers) =>
            CompanyActions.loadOperatorUsersSuccess({ operatorUsers }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.loadOperatorUsersFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage: 'Failed to load operator users. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly loadOperatorUsersOnBuyerDetail$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.loadDetailSuccess),
      filter(({ detail }) => detail.isABuyer || detail.isFusion),
      map(() => CompanyActions.loadOperatorUsers()),
    ),
  );

  public readonly loadOperatorUsersOnCreate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.startCreate),
      map(() => CompanyActions.loadOperatorUsers()),
    ),
  );

  public readonly loadCurrencies$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.loadCurrencies),
      switchMap(() =>
        this._service.currencies().pipe(
          map((currencies) =>
            CompanyActions.loadCurrenciesSuccess({ currencies }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.loadCurrenciesFailure({ error: error.message }),
              ),
            {
              notify: true,
              customMessage: 'Failed to load currencies. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly saveCompany$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.saveCompany),
      exhaustMap(({ payload }) =>
        this._service.saveCompany(payload).pipe(
          tap((result) => this._toastService.success(result.message)),
          map((result) => CompanyActions.saveCompanySuccess({ id: result.id })),
          this.psCatchError(
            (error) =>
              of(CompanyActions.saveCompanyFailure({ error: error.message })),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly reloadAfterCreate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.saveCompanySuccess),
      map(({ id }) => CompanyActions.reloadDetail({ id })),
    ),
  );

  public readonly updateCompany$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.updateCompany),
      exhaustMap(({ id, payload }) =>
        this._service.updateCompany(id, payload).pipe(
          tap((message) => {
            this._toastService.success(message);
            // The backend turns the feature off for a Monthly buyer (invoice emails only exist for PO / Project).
            if (payload.isABuyer && payload.generateInvoice === 'MONTHLY') {
              this._toastService.info(
                MANDATORY_PM_EMAILS_INVOICE_TURNED_OFF_MESSAGE,
              );
            }
          }),
          switchMap(() => [
            CompanyActions.updateCompanySuccess(),
            CompanyActions.reloadDetail({ id }),
          ]),
          this.psCatchError(
            (error) =>
              of(CompanyActions.updateCompanyFailure({ error: error.message })),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly deleteCompany$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.deleteCompany),
      exhaustMap(({ id }) =>
        this._service.deleteCompany(id).pipe(
          tap(() =>
            this._toastService.success('Company has been successfully deleted'),
          ),
          map(() => CompanyActions.deleteCompanySuccess()),
          this.psCatchError(
            (error) =>
              of(CompanyActions.deleteCompanyFailure({ error: error.message })),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly saveSupplierSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.saveSupplierSettings),
      exhaustMap(({ id, settings }) =>
        this._supplierSettingsService.updateSupplierSettings(id, settings).pipe(
          tap((message) => this._toastService.success(message)),
          map(() => CompanyActions.saveSupplierSettingsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.saveSupplierSettingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to save supplier settings. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly enableSegmentation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.enableSegmentation),
      exhaustMap(({ id }) =>
        this._supplierSettingsService.enableSegmentation(id).pipe(
          map(() => CompanyActions.enableSegmentationSuccess({ id })),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.enableSegmentationFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage: 'Failed to enable segmentation. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly disableSegmentation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.disableSegmentation),
      exhaustMap(({ id }) =>
        this._supplierSettingsService.disableSegmentation(id).pipe(
          map(() => CompanyActions.disableSegmentationSuccess({ id })),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.disableSegmentationFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to disable segmentation. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly loadSegmentParentsAfterEnable$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.enableSegmentationSuccess),
      map(({ id }) => CompanyActions.loadSegmentParents({ id })),
    ),
  );

  public readonly loadSegmentParents$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.loadSegmentParents),
      switchMap(({ id }) =>
        this._segmentationService.segmentParents(id).pipe(
          map((segmentation) =>
            CompanyActions.loadSegmentParentsSuccess({ segmentation }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.loadSegmentParentsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to load segmentation parents. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly invalidateAndReloadList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        CompanyActions.saveCompanySuccess,
        CompanyActions.updateCompanySuccess,
        CompanyActions.deleteCompanySuccess,
      ),
      switchMap(() => [
        CompanyActions.invalidateCache(),
        CompanyActions.loadCompanies({}),
      ]),
    ),
  );

  public readonly saveSegmentParents$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.saveSegmentParents),
      exhaustMap(({ id, operations }) =>
        this._segmentationService.updateSegmentParents(id, operations).pipe(
          map(() => CompanyActions.saveSegmentParentsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.saveSegmentParentsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to save segmentation parents. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly loadBuyerConfigSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.loadBuyerConfigSettings),
      switchMap(({ id }) =>
        this._buyerConfigService.buyerConfigSettings(id).pipe(
          map((settings) =>
            CompanyActions.loadBuyerConfigSettingsSuccess({ settings }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.loadBuyerConfigSettingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to load buyer config settings. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly saveBuyerConfigSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.saveBuyerConfigSettings),
      exhaustMap(({ payload }) =>
        this._buyerConfigService.saveBuyerConfigSettings(payload).pipe(
          tap((message) => this._toastService.success(message)),
          map(() => CompanyActions.saveBuyerConfigSettingsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.saveBuyerConfigSettingsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly saveBuyerCountrySettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.saveBuyerCountrySettings),
      exhaustMap(({ payload }) =>
        this._buyerConfigService.saveBuyerCountrySettings(payload).pipe(
          tap((message) => this._toastService.success(message)),
          map(() => CompanyActions.saveBuyerCountrySettingsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.saveBuyerCountrySettingsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly resetBuyerConfigSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.resetBuyerConfigSettings),
      exhaustMap(({ id }) =>
        this._buyerConfigService.resetToDefault(id).pipe(
          tap((message) => this._toastService.success(message)),
          map((message) =>
            CompanyActions.resetBuyerConfigSettingsSuccess({ id, message }),
          ),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.resetBuyerConfigSettingsFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Failed to reset buyer config settings. Please try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly reloadBuyerConfigSettingsAfterReset$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.resetBuyerConfigSettingsSuccess),
      map(({ id }) => CompanyActions.loadBuyerConfigSettings({ id })),
    ),
  );

  public readonly registerWebhook$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.registerWebhook),
      exhaustMap(({ buyerId, webhook }) =>
        this._buyerConfigNotificationService
          .registerWebhook(buyerId, webhook)
          .pipe(
            map(() => CompanyActions.registerWebhookSuccess()),
            this.psCatchError(
              (error) =>
                of(
                  CompanyActions.registerWebhookFailure({
                    error: error.message,
                  }),
                ),
              {
                notify: true,
                customMessage: 'Failed to register webhook. Please try again.',
              },
            ),
          ),
      ),
    ),
  );

  public readonly unsubscribeWebhook$ = createEffect(() =>
    this._actions$.pipe(
      ofType(CompanyActions.unsubscribeWebhook),
      exhaustMap(({ buyerId }) =>
        this._buyerConfigNotificationService.unsubscribe(buyerId).pipe(
          map(() => CompanyActions.unsubscribeWebhookSuccess()),
          this.psCatchError(
            (error) =>
              of(
                CompanyActions.unsubscribeWebhookFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage: 'Failed to unsubscribe webhook. Please try again.',
            },
          ),
        ),
      ),
    ),
  );
}
